import { useEffect, useMemo, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { IconDeviceFloppy, IconPlus } from "@tabler/icons-react";
import { getIn, useFormik } from "formik";
import * as yup from "yup";
import "quill/dist/quill.snow.css";
import InlineError from "@components/forms/InlineError";
import AppModal from "@components/modals/AppModal";
import ConfirmDeleteModal from "@components/modals/ConfirmDeleteModal";
import useLanguages from "@hooks/languages/useLanguages";
import SortableCarbonItem from "@components/home_content/sections/carbon/SortableCarbonItem";
import {
  deleteHomeContentFaqRequest,
  listHomeContentFaqsRequest,
  saveHomeContentFaqsRequest,
} from "@services/homeContent/homeContentFaqsApi";
import {
  createQuillEditor,
  destroyQuillEditor,
  getQuillHtml,
} from "@utils/quill/editor";

function buildLanguageList(languages = []) {
  return languages
    .map((language) => ({
      id: language.lng_id || language.id,
      iso: (language.lng_iso || language.iso || "").toLowerCase(),
      name: language.lng_name || language.name || "",
    }))
    .filter((language) => language.iso);
}

function createClientId() {
  return `faq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createEmptyLocalizedMessage(languageList = []) {
  return languageList.reduce((acc, language) => {
    acc[language.iso] = {
      id: "",
      message: "",
    };
    return acc;
  }, {});
}

function createEmptyFaqItem(languageList = [], order = 1) {
  return {
    clientId: createClientId(),
    id: "",
    order,
    question: createEmptyLocalizedMessage(languageList),
    answer: createEmptyLocalizedMessage(languageList),
  };
}

function normalizeLocalizedMessage(value, languageList = []) {
  return languageList.reduce((acc, language) => {
    const localizedValue = value?.[language.iso];
    if (
      localizedValue &&
      typeof localizedValue === "object" &&
      !Array.isArray(localizedValue)
    ) {
      acc[language.iso] = {
        id: localizedValue.id || "",
        message: localizedValue.message || "",
      };
      return acc;
    }

    acc[language.iso] = {
      id: "",
      message: typeof localizedValue === "string" ? localizedValue : "",
    };
    return acc;
  }, {});
}

function normalizeFaqItems(items = [], languageList = []) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    clientId: item?.clientId || createClientId(),
    id: item?.id || "",
    order: Number(item?.order) || index + 1,
    question: normalizeLocalizedMessage(item?.question, languageList),
    answer: normalizeLocalizedMessage(item?.answer, languageList),
  }));
}

function buildInitialValues(languageList = [], items = []) {
  return {
    items: normalizeFaqItems(items, languageList),
  };
}

function buildValidationSchema(languageList = []) {
  const localizedShape = languageList.reduce((acc, language) => {
    acc[language.iso] = yup.object({
      id: yup.string().default(""),
      message: yup
        .string()
        .trim()
        .required(`${language.iso.toUpperCase()} is required.`),
    });
    return acc;
  }, {});

  return yup.object({
    items: yup.array().of(
      yup.object({
        question: yup.object().shape(localizedShape),
        answer: yup.object().shape(localizedShape),
      }),
    ),
  });
}

function buildTouchedValues(items = [], languageList = []) {
  return {
    items: items.map(() => ({
      question: languageList.reduce((acc, language) => {
        acc[language.iso] = {
          message: true,
        };
        return acc;
      }, {}),
      answer: languageList.reduce((acc, language) => {
        acc[language.iso] = {
          message: true,
        };
        return acc;
      }, {}),
    })),
  };
}

function buildFaqsPayload(items = [], languageList = []) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    id: item?.id || "",
    question: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        id: item?.question?.[language.iso]?.id || "",
        message: item?.question?.[language.iso]?.message || "",
      };
      return acc;
    }, {}),
    answer: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        id: item?.answer?.[language.iso]?.id || "",
        message: item?.answer?.[language.iso]?.message || "",
      };
      return acc;
    }, {}),
    order: index + 1,
  }));
}

function FaqsSection() {
  const { languages, loading, error } = useLanguages();
  const languageList = useMemo(() => buildLanguageList(languages), [languages]);
  const [faqItems, setFaqItems] = useState([]);
  const initialValues = useMemo(
    () => buildInitialValues(languageList, faqItems),
    [faqItems, languageList],
  );
  const validationSchema = useMemo(
    () => buildValidationSchema(languageList),
    [languageList],
  );
  const [activeLanguageIso, setActiveLanguageIso] = useState("");
  const [expandedItemId, setExpandedItemId] = useState("");
  const [deleteTargetItem, setDeleteTargetItem] = useState(null);
  const [fetchingFaqs, setFetchingFaqs] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState("");
  const [editor, setEditor] = useState({
    isOpen: false,
    itemIndex: -1,
    languageIso: "",
  });
  const [answerHtml, setAnswerHtml] = useState("");
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [quillHost, setQuillHost] = useState(null);
  const quillInstanceRef = useRef(null);
  const quillHandlerRef = useRef(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const currentLanguageIso = languageList.some(
    (language) => language.iso === activeLanguageIso,
  )
    ? activeLanguageIso
    : languageList[0]?.iso || "";

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setSubmitting(true);
      setLocalError("");
      try {
        const response = await saveHomeContentFaqsRequest(
          buildFaqsPayload(values?.items || [], languageList),
        );
        setFaqItems(Array.isArray(response?.body) ? response.body : []);
      } catch (submitError) {
        setLocalError(submitError?.message || "Could not save FAQs.");
        throw submitError;
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    let isMounted = true;
    async function fetchFaqs() {
      setFetchingFaqs(true);
      setLocalError("");
      try {
        const response = await listHomeContentFaqsRequest();
        if (!isMounted) return;
        setFaqItems(Array.isArray(response?.body) ? response.body : []);
      } catch (fetchError) {
        if (!isMounted) return;
        setLocalError(fetchError?.message || "Could not load FAQs.");
      } finally {
        if (isMounted) setFetchingFaqs(false);
      }
    }
    fetchFaqs();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!languageList.length) return;
    const firstItemId = formik.values?.items?.[0]?.clientId || "";
    if (firstItemId) setExpandedItemId((current) => current || firstItemId);
    formik.setTouched(
      buildTouchedValues(formik.values?.items || [], languageList),
      true,
    );
  }, [initialValues, languageList]);

  function getFieldError(path) {
    const touched = getIn(formik.touched, path);
    const fieldError = getIn(formik.errors, path);
    return touched ? fieldError || "" : "";
  }

  function updateLocalizedField(index, fieldName, languageIso, nextValue) {
    formik.setFieldValue(
      `items.${index}.${fieldName}.${languageIso}.message`,
      nextValue,
      true,
    );
  }

  function openAnswerEditor(itemIndex, languageIso, value = "") {
    setEditor({
      isOpen: true,
      itemIndex,
      languageIso,
    });
    setAnswerHtml(value || "");
  }

  function closeAnswerEditor() {
    setEditor({
      isOpen: false,
      itemIndex: -1,
      languageIso: "",
    });
    setIsEditorReady(false);
  }

  function saveAnswerEditor() {
    if (editor.itemIndex < 0 || !editor.languageIso) return;
    formik.setFieldValue(
      `items.${editor.itemIndex}.answer.${editor.languageIso}.message`,
      answerHtml,
      true,
    );
    formik.setFieldTouched(
      `items.${editor.itemIndex}.answer.${editor.languageIso}.message`,
      true,
      true,
    );
    closeAnswerEditor();
  }

  function reorderWithOrder(items = []) {
    return items.map((item, index) => ({ ...item, order: index + 1 }));
  }

  function handleAddItem() {
    const nextItems = reorderWithOrder([
      ...(formik.values?.items || []),
      createEmptyFaqItem(languageList, (formik.values?.items?.length || 0) + 1),
    ]);
    formik.setFieldValue("items", nextItems, true);
    setExpandedItemId(nextItems[nextItems.length - 1]?.clientId || "");
  }

  function handleRemoveItem(itemClientId) {
    const removedItem = (formik.values?.items || []).find(
      (item) => item.clientId === itemClientId,
    );
    if (!removedItem) return;
    setDeleteTargetItem(removedItem);
  }

  function handleDeleteLocalItem(itemClientId) {
    const nextItems = reorderWithOrder(
      (formik.values?.items || []).filter((item) => item.clientId !== itemClientId),
    );
    formik.setFieldValue("items", nextItems, true);
    setExpandedItemId((current) =>
      current === itemClientId ? nextItems[0]?.clientId || "" : current,
    );
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) return;
    const items = [...(formik.values?.items || [])];
    const oldIndex = items.findIndex((item) => item.clientId === active.id);
    const newIndex = items.findIndex((item) => item.clientId === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    formik.setFieldValue(
      "items",
      reorderWithOrder(arrayMove(items, oldIndex, newIndex)),
      true,
    );
  }

  async function handleDeletePersistedItem(item) {
    if (!item?.id) return;
    setSubmitting(true);
    setLocalError("");
    try {
      await deleteHomeContentFaqRequest(item.id);
      const nextItems = reorderWithOrder(
        (formik.values?.items || []).filter(
          (currentItem) => currentItem.clientId !== item.clientId,
        ),
      );
      setFaqItems(nextItems);
      formik.setFieldValue("items", nextItems, true);
      setExpandedItemId((current) =>
        current === item.clientId ? nextItems[0]?.clientId || "" : current,
      );
    } catch (deleteError) {
      setLocalError(deleteError?.message || "Could not delete FAQ.");
    } finally {
      setDeleteTargetItem(null);
      setSubmitting(false);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteTargetItem) return;
    if (deleteTargetItem?.id) {
      await handleDeletePersistedItem(deleteTargetItem);
      return;
    }
    handleDeleteLocalItem(deleteTargetItem.clientId);
    setDeleteTargetItem(null);
  }

  async function handleSave() {
    formik.setTouched(
      buildTouchedValues(formik.values?.items || [], languageList),
      true,
    );
    try {
      await formik.submitForm();
    } catch (error) {
    }
  }

  useEffect(() => {
    if (!editor.isOpen) return;
    if (!quillHost || quillInstanceRef.current) return;
    const quill = createQuillEditor({
      container: quillHost,
      initialHtml: answerHtml,
    });
    if (!quill) return;
    quillHandlerRef.current = () => setAnswerHtml(getQuillHtml(quill));
    quill.on("text-change", quillHandlerRef.current);
    quillInstanceRef.current = quill;
    setIsEditorReady(true);
  }, [answerHtml, editor.isOpen, quillHost]);

  useEffect(() => {
    if (!editor.isOpen && quillInstanceRef.current) {
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
      setIsEditorReady(false);
      setQuillHost(null);
    }
  }, [editor.isOpen, quillHost]);

  useEffect(
    () => () => {
      if (!quillInstanceRef.current) return;
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
    },
    [quillHost],
  );

  return (
    <section className="home-content-section-card">
      <div className="home-content-form-head">
        <div>
          <h3>FAQs</h3>
          <p>Manage the localized FAQ entries with question and answer.</p>
        </div>
        {languageList.length ? (
          <div className="app-language-section">
            <div className="app-language-switcher" aria-label="Languages">
              {languageList.map((language) => (
                <button
                  key={language.id || language.iso}
                  type="button"
                  className={`app-language-btn ${currentLanguageIso === language.iso ? "is-active" : ""}`}
                  onClick={() => setActiveLanguageIso(language.iso)}
                  aria-pressed={currentLanguageIso === language.iso}
                  title={language.name || language.iso.toUpperCase()}
                >
                  {language.iso.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {loading || fetchingFaqs ? <p className="form-helper">Loading data...</p> : null}
      {error ? <p className="message-error">{error}</p> : null}
      {localError ? <p className="message-error">{localError}</p> : null}

      {!loading && !fetchingFaqs && languageList.length ? (
        <form className="home-content-form tour-add-form" onSubmit={formik.handleSubmit}>
          <div className="home-content-form-shell">
            <div className="home-content-inline-actions">
              <button
                type="button"
                className="app-icon-btn app-icon-btn--add"
                aria-label="Add faq item"
                title="Add faq item"
                onClick={handleAddItem}
              >
                <IconPlus size={16} />
              </button>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={(formik.values?.items || []).map((item) => item.clientId)}
                strategy={verticalListSortingStrategy}
              >
                <div className="home-content-accordion-list">
                  {(formik.values?.items || []).map((item, index) => (
                    <SortableCarbonItem
                      key={item.clientId}
                      item={{
                        ...item,
                        id: item.clientId,
                        title: languageList.reduce((acc, language) => {
                          acc[language.iso] =
                            item?.question?.[language.iso]?.message || "";
                          return acc;
                        }, {}),
                      }}
                      visibleLanguageIso={currentLanguageIso}
                      isOpen={expandedItemId === item.clientId}
                      onToggle={() =>
                        setExpandedItemId((current) =>
                          current === item.clientId ? "" : item.clientId,
                        )
                      }
                      onDelete={() => handleRemoveItem(item.clientId)}
                    >
                      <div className="home-content-form-grid">
                        <div className="tour-add-form-group">
                          <p className="tour-add-form-label">
                            question
                            {getFieldError(
                              `items.${index}.question.${currentLanguageIso}.message`,
                            ) ? (
                              <InlineError>
                                {getFieldError(
                                  `items.${index}.question.${currentLanguageIso}.message`,
                                )}
                              </InlineError>
                            ) : null}
                          </p>
                          <label className="field-label app-form-field">
                            <input
                              className="field-input"
                              value={
                                item?.question?.[currentLanguageIso]?.message || ""
                              }
                              placeholder={`Write question (${currentLanguageIso})`}
                              onChange={(event) =>
                                updateLocalizedField(
                                  index,
                                  "question",
                                  currentLanguageIso,
                                  event.target.value,
                                )
                              }
                              onBlur={() =>
                                formik.setFieldTouched(
                                  `items.${index}.question.${currentLanguageIso}.message`,
                                  true,
                                  true,
                                )
                              }
                            />
                          </label>
                        </div>

                        <div className="tour-add-form-group">
                          <p className="tour-add-form-label">
                            answer
                            {getFieldError(
                              `items.${index}.answer.${currentLanguageIso}.message`,
                            ) ? (
                              <InlineError>
                                {getFieldError(
                                  `items.${index}.answer.${currentLanguageIso}.message`,
                                )}
                              </InlineError>
                            ) : null}
                          </p>
                          <button
                            type="button"
                            className={`field-label app-form-field field-preview-row ${
                              item?.answer?.[currentLanguageIso]?.message
                                ? "filled"
                                : ""
                            }`}
                            onClick={() =>
                              openAnswerEditor(
                                index,
                                currentLanguageIso,
                                item?.answer?.[currentLanguageIso]?.message || "",
                              )
                            }
                          >
                            {item?.answer?.[currentLanguageIso]?.message ? (
                              <span
                                className="field-preview-value field-preview-value-html"
                                dangerouslySetInnerHTML={{
                                  __html: String(
                                    item?.answer?.[currentLanguageIso]
                                      ?.message || "",
                                  ),
                                }}
                              />
                            ) : (
                              <span className="field-preview-value">
                                Complete
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    </SortableCarbonItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          <div className="mode-route-tool-steps tour-add-save-row">
            <button
              type="button"
              className={`app-icon-btn app-icon-btn--add mode-route-tool-route-btn ${!formik.isValid || submitting ? "is-disabled" : ""} ${submitting ? "is-loading" : ""}`}
              onClick={handleSave}
              disabled={!formik.isValid || submitting}
              aria-disabled={!formik.isValid || submitting}
              aria-label="Save faqs content"
              title="Save faqs content"
            >
              {submitting ? (
                <span className="mode-route-tool-btn-spinner" />
              ) : (
                <>
                  Save
                  <IconDeviceFloppy size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      ) : null}

      <AppModal
        isOpen={editor.isOpen}
        onClose={closeAnswerEditor}
        title={`answer (${editor.languageIso})`}
        size="lg"
      >
        <div className="tour-add-editor">
          {!isEditorReady ? (
            <p className="tour-add-editor-loading">Loading editor...</p>
          ) : null}
          <div ref={setQuillHost} className="tour-add-quill" />
          <div className="tour-add-editor-actions">
            <button
              type="button"
              className="button-primary"
              onClick={saveAnswerEditor}
            >
              Save
            </button>
            <button
              type="button"
              className="button-ghost"
              onClick={closeAnswerEditor}
            >
              Cancel
            </button>
          </div>
        </div>
      </AppModal>

      <ConfirmDeleteModal
        isOpen={Boolean(deleteTargetItem)}
        onClose={() => {
          if (submitting) return;
          setDeleteTargetItem(null);
        }}
        onConfirm={handleConfirmDelete}
        submitting={submitting}
        title="Delete faq"
        description="Are you sure you want to delete this faq?"
        preview={
          deleteTargetItem?.question?.en?.message ? (
            <p>
              <strong>{deleteTargetItem.question.en.message}</strong>
            </p>
          ) : null
        }
      />
    </section>
  );
}

export default FaqsSection;
